import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, Search, X, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { currencyMasterApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { CurrencyListItem, CreateCurrencyRequest, UpdateCurrencyRequest } from '@/types'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_STATUSES = '__all__'

interface CurrencyFilters {
  searchTerm: string
  status: string
}

export default function CurrenciesPage() {
  const { t } = useTranslation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingCurrency, setEditingCurrency] = useState<CurrencyListItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [filters, setFilters] = useState<CurrencyFilters>({
    searchTerm: '',
    status: ALL_STATUSES,
  })
  const [formData, setFormData] = useState<CreateCurrencyRequest>({
    currency_code: '',
    currency_name: '',
    currency_symbol: '',
    decimal_places: 2,
  })

  const queryClient = useQueryClient()

  const { data: currencies = [], isLoading } = useQuery({
    queryKey: ['admin-currencies'],
    queryFn: () => currencyMasterApi.getAllAdmin(),
  })

  // Filter currencies based on filters
  const filteredCurrencies = useMemo(() => {
    return currencies.filter(currency => {
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase()
        const matchesName = currency.currency_name.toLowerCase().includes(searchLower)
        const matchesCode = currency.currency_code.toLowerCase().includes(searchLower)
        const matchesSymbol = currency.currency_symbol.toLowerCase().includes(searchLower)
        if (!matchesName && !matchesCode && !matchesSymbol) return false
      }
      if (filters.status !== ALL_STATUSES) {
        const isActive = filters.status === 'active'
        if (currency.is_active !== isActive) return false
      }
      return true
    })
  }, [currencies, filters])

  // Pagination calculations
  const totalRecords = filteredCurrencies.length
  const totalPages = Math.ceil(totalRecords / pageSize)
  const paginatedCurrencies = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredCurrencies.slice(startIndex, startIndex + pageSize)
  }, [filteredCurrencies, currentPage, pageSize])

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1)
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }

  const clearFilters = () => {
    setFilters({ searchTerm: '', status: ALL_STATUSES })
    setCurrentPage(1)
  }

  const hasActiveFilters = !!(filters.searchTerm || filters.status !== ALL_STATUSES)

  const createMutation = useMutation({
    mutationFn: currencyMasterApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-currencies'] })
      queryClient.invalidateQueries({ queryKey: ['currencies'] })
      setIsDialogOpen(false)
      resetForm()
      toast.success(t('masters.currency.currencyCreated'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.currency.failedToCreate')
      toast.error(errorMessage)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCurrencyRequest }) =>
      currencyMasterApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-currencies'] })
      queryClient.invalidateQueries({ queryKey: ['currencies'] })
      setIsDialogOpen(false)
      setEditingCurrency(null)
      resetForm()
      toast.success(t('masters.currency.currencyUpdated'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.currency.failedToUpdate')
      toast.error(errorMessage)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: currencyMasterApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-currencies'] })
      queryClient.invalidateQueries({ queryKey: ['currencies'] })
      toast.success(t('masters.currency.currencyDeleted'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.currency.failedToDelete')
      toast.error(errorMessage)
    },
  })

  const resetForm = () => {
    setFormData({ currency_code: '', currency_name: '', currency_symbol: '', decimal_places: 2 })
    setEditingCurrency(null)
  }

  const handleEdit = (currency: CurrencyListItem) => {
    setEditingCurrency(currency)
    setFormData({
      currency_code: currency.currency_code,
      currency_name: currency.currency_name,
      currency_symbol: currency.currency_symbol,
      decimal_places: currency.decimal_places,
    })
    setIsDialogOpen(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingCurrency) {
      updateMutation.mutate({ id: editingCurrency.currency_id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleOpenChange = (open: boolean) => {
    setIsDialogOpen(open)
    if (!open) resetForm()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('masters.currency.title')}</h1>
          <p className="text-muted-foreground">{t('masters.currency.subtitle')}</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t('masters.currency.addCurrency')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>{editingCurrency ? t('masters.currency.editCurrency') : t('masters.currency.addCurrency')}</DialogTitle>
                <DialogDescription>
                  {editingCurrency ? t('masters.currency.updateCurrencyConfig') : t('masters.currency.createNewCurrencyConfig')}
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="currency_code">{t('masters.currency.currencyCode')} *</Label>
                  <Input
                    id="currency_code"
                    value={formData.currency_code}
                    onChange={(e) => setFormData({ ...formData, currency_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., USD, EUR, INR"
                    maxLength={3}
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="currency_name">{t('masters.currency.currencyName')} *</Label>
                  <Input
                    id="currency_name"
                    value={formData.currency_name}
                    onChange={(e) => setFormData({ ...formData, currency_name: e.target.value })}
                    placeholder="e.g., US Dollar, Euro"
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="currency_symbol">{t('masters.currency.currencySymbol')} *</Label>
                  <Input
                    id="currency_symbol"
                    value={formData.currency_symbol}
                    onChange={(e) => setFormData({ ...formData, currency_symbol: e.target.value })}
                    placeholder="e.g., $, €, ₹"
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="decimal_places">{t('masters.currency.decimalPlaces')}</Label>
                  <Input
                    id="decimal_places"
                    type="number"
                    min={0}
                    max={4}
                    value={formData.decimal_places ?? 2}
                    onChange={(e) => setFormData({ ...formData, decimal_places: parseInt(e.target.value) || 2 })}
                    placeholder="e.g., 2"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending
                    ? t('common.saving')
                    : editingCurrency
                    ? t('masters.currency.updateCurrency')
                    : t('masters.currency.createCurrency')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center -mb-1">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none bg-white dark:bg-neutral-800 rounded-sm">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            placeholder={t('masters.currency.searchPlaceholder')}
            value={filters.searchTerm}
            onChange={(e) => {
              setFilters({ ...filters, searchTerm: e.target.value })
              setCurrentPage(1)
            }}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.status}
          onValueChange={(value) => {
            setFilters({ ...filters, status: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('common.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('common.allStatuses')}</SelectItem>
            <SelectItem value="active">{t('common.active')}</SelectItem>
            <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      <CurrencyTable
        currencies={paginatedCurrencies}
        allCurrencies={currencies}
        filteredCurrencies={filteredCurrencies}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={(id) => deleteMutation.mutate(id)}
        isDeleting={deleteMutation.isPending}
        clearFilters={clearFilters}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRecords={totalRecords}
        totalPages={totalPages}
        startRecord={startRecord}
        endRecord={endRecord}
        onPageSizeChange={handlePageSizeChange}
        onNextPage={goToNextPage}
        onPreviousPage={goToPreviousPage}
        t={t}
      />
    </div>
  )
}


function CurrencyTable({
  currencies,
  allCurrencies,
  filteredCurrencies,
  isLoading,
  onEdit,
  onDelete,
  isDeleting,
  clearFilters,
  pageSize,
  currentPage,
  totalRecords,
  totalPages,
  startRecord,
  endRecord,
  onPageSizeChange,
  onNextPage,
  onPreviousPage,
  t,
}: {
  currencies: CurrencyListItem[]
  allCurrencies: CurrencyListItem[]
  filteredCurrencies: CurrencyListItem[]
  isLoading: boolean
  onEdit: (currency: CurrencyListItem) => void
  onDelete: (id: string) => void
  isDeleting: boolean
  clearFilters: () => void
  pageSize: number
  currentPage: number
  totalRecords: number
  totalPages: number
  startRecord: number
  endRecord: number
  onPageSizeChange: (size: string) => void
  onNextPage: () => void
  onPreviousPage: () => void
  t: (key: string) => string
}) {
  if (isLoading) {
    return <div className="text-center py-8">{t('common.loading')}</div>
  }

  if (allCurrencies.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('masters.currency.noCurrenciesFound')}
        </CardContent>
      </Card>
    )
  }

  if (filteredCurrencies.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="text-muted-foreground mb-4">
            {t('masters.currency.noMatchingCurrencies')}
          </div>
          <Button variant="outline" onClick={clearFilters}>
            <X className="mr-2 h-4 w-4" />
            {t('common.reset')} {t('common.filters')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-280px)]">
      <Card className="flex-1 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>{t('masters.currency.currencyCode')}</TableHead>
                <TableHead>{t('masters.currency.currencyName')}</TableHead>
                <TableHead>{t('masters.currency.currencySymbol')}</TableHead>
                <TableHead>{t('masters.currency.decimalPlaces')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead className="w-[120px]">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currencies.map((currency) => (
                <TableRow key={currency.currency_id}>
                  <TableCell className="font-medium">{currency.currency_code}</TableCell>
                  <TableCell>{currency.currency_name}</TableCell>
                  <TableCell>{currency.currency_symbol}</TableCell>
                  <TableCell>{currency.decimal_places}</TableCell>
                  <TableCell>
                    {currency.is_active ? (
                      <Badge variant="default">{t('common.active')}</Badge>
                    ) : (
                      <Badge variant="secondary">{t('common.inactive')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(currency)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          {t('common.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onDelete(currency.currency_id)}
                          disabled={isDeleting}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Pagination Controls */}
        <div className="flex items-center gap-3 px-4 py-2 border-t">
          <Select value={String(pageSize)} onValueChange={onPageSizeChange}>
            <SelectTrigger className="w-[70px] h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">
            {startRecord}-{endRecord} {t('common.of')} {totalRecords}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onPreviousPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onNextPage}
              disabled={currentPage >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

